import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/queries'
import { invoiceFetch as apiFetch } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { invoiceKeys } from '@/features/invoices/keys'
import { USE_MOCK_AP_INVOICES, mockCreditMemoOverrides } from '../mock'
import type {
  ApVendorAlias,
  ApVendorCreditMemoInput,
  ApVendorCreditApplicationInput,
} from '../types'
import type { ApVendorSummary, ApVendorMergeReview, ApVendorCreditMemo } from '../view-models'

export function useCreateVendorAlias(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { alias: string; reason: string }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApVendorAlias>(token, `/dashboard/ap-invoices/vendors/${vendorId}/aliases`, {
        method: 'POST',
        json: body,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorAliases(vendorId),
      })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorAudit(vendorId),
      })
    },
  })
}

export function useUpdateApVendor(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      contact_email?: string | null
      contact_phone?: string | null
      payment_terms_raw?: string | null
      tax_policy?: 'unknown' | 'tax_exempt' | 'taxable' | 'vendor_specific'
      tax_policy_reason?: string | null
      notes?: string | null
      change_reason: string
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApVendorSummary>(token, `/dashboard/ap-invoices/vendors/${vendorId}`, {
        method: 'PATCH',
        json: body,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendor(vendorId),
      })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorAudit(vendorId),
      })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
    },
  })
}

export function useDeactivateVendorAlias(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ aliasId, reason }: { aliasId: number; reason: string }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApVendorAlias>(
        token,
        `/dashboard/ap-invoices/vendors/${vendorId}/aliases/${aliasId}/deactivate`,
        { method: 'PATCH', json: { reason } },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorAliases(vendorId),
      })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorAudit(vendorId),
      })
    },
  })
}

export function useRequestVendorMerge(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: {
      source_vendor_id: number
      target_vendor_id: number
      reason: string
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApVendorMergeReview>(token, '/dashboard/ap-invoices/vendor-merge-reviews', {
        method: 'POST',
        json: body,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorMergeReviews(vendorId),
      })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorAudit(vendorId),
      })
    },
  })
}

export function useDecideVendorMerge(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      reviewUuid,
      decision,
      reason,
    }: {
      reviewUuid: string
      decision: 'approved' | 'rejected' | 'cancelled'
      reason: string
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApVendorMergeReview>(
        token,
        `/dashboard/ap-invoices/vendor-merge-reviews/${reviewUuid}`,
        { method: 'PATCH', json: { decision, reason } },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorMergeReviews(vendorId),
      })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorAudit(vendorId),
      })
    },
  })
}

export function useExecuteVendorMerge(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      reviewUuid,
      sourceVendorId,
      targetVendorId,
      reason,
    }: {
      reviewUuid: string
      sourceVendorId: number
      targetVendorId: number
      reason: string
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<Record<string, unknown>>(
        token,
        `/dashboard/ap-invoices/vendor-merge-reviews/${reviewUuid}/execute`,
        {
          method: 'POST',
          json: {
            confirm_source_vendor_id: sourceVendorId,
            confirm_target_vendor_id: targetVendorId,
            reason,
          },
        },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendor(vendorId),
      })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorMergeReviews(vendorId),
      })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorAudit(vendorId),
      })
    },
  })
}

export function useCreateVendorCreditMemo(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ApVendorCreditMemoInput) => {
      if (USE_MOCK_AP_INVOICES) {
        const id = Number(vendorId)
        const timestamp = new Date().toISOString()
        const memo: ApVendorCreditMemo = {
          id: Math.floor(Math.random() * 100000),
          uuid: `credit-demo-${Date.now()}`,
          vendor_id: Number.isFinite(id) ? id : 0,
          invoice_id: body.invoice_id ?? null,
          invoice_uuid: body.invoice_uuid ?? null,
          amount: body.amount,
          direction: body.direction,
          status: body.status ?? 'open',
          memo_raw: body.memo_raw ?? null,
          source: 'manual',
          created_by: 'mock-ap-user',
          derived: false,
          created_at: timestamp,
          updated_at: timestamp,
        }
        const key = String(vendorId)
        mockCreditMemoOverrides.set(key, [...(mockCreditMemoOverrides.get(key) ?? []), memo])
        return memo
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApVendorCreditMemo>(
        token,
        `/dashboard/ap-invoices/vendors/${vendorId}/credits`,
        {
          method: 'POST',
          json: body,
        },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorCredits(vendorId),
      })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorInvoices(vendorId),
      })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
    },
  })
}

export function useApplyVendorCredit(vendorId: number | string | undefined) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      memoUuid,
      body,
    }: {
      memoUuid: string
      body: ApVendorCreditApplicationInput
    }) => {
      if (USE_MOCK_AP_INVOICES) {
        return {
          application: {
            id: Math.floor(Math.random() * 100000),
            uuid: `credit-application-${Date.now()}`,
            vendor_id: Number(vendorId) || 0,
            invoice_uuid: body.invoice_uuid,
            amount: body.amount,
            direction: 'credit_applied',
            status: 'applied',
            memo_raw: body.reason,
            source: 'manual',
            idempotency_key: body.idempotency_key,
            derived: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          } satisfies ApVendorCreditMemo,
          credit_remaining: 0,
          invoice_remaining: 0,
          payment_execution: 'not_performed',
        }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<{
        application: ApVendorCreditMemo
        credit_remaining: string | number
        invoice_remaining: string | number
        payment_execution: 'not_performed'
      }>(token, `/dashboard/ap-invoices/vendors/${vendorId}/credits/${memoUuid}/apply`, {
        method: 'POST',
        json: body,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.vendorCredits(vendorId),
      })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
    },
  })
}
